import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import moment from 'moment';
import { MuniEnvironmentParams } from '@ipreo/muni-shared';
import { omit } from 'lodash';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import {
  USTreasuryResponse,
  USTreasuryResponsePart,
  USTreasuries,
  USTreasury,
  USTreasuryData
} from '../models/us-treasury';

@Injectable()
export class TreasuriesService {
  private muniApiHost: string;

  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.muniApiHost = (config.params as MuniEnvironmentParams).apiHost;
  }

  public fetchData(date: Date): Observable<USTreasuries> {
    const requestedDate: string = moment(date).format('MM-DD-YYYY');

    return this.http
      .get(`${this.muniApiHost}/api/v1/us-treasuries?date=${requestedDate}`)
      .pipe(
        map((res: USTreasuryResponse[]) =>
          this.usTreasuriesResponseToViewModel(res)
        )
      );
  }

  public usTreasuriesResponseToViewModel(
    treasuries: USTreasuryResponse[]
  ): USTreasuries {
    return treasuries
      .map((response: USTreasuryResponse) =>
        this.usTreasuryResponseToViewModel(response)
      )
      .sort((t1: USTreasury, t2: USTreasury) => {
        const reg = /US(\d+)(\w)/;
        const t1Matches: RegExpMatchArray = t1.securityId.match(reg);
        const t2Matches: RegExpMatchArray = t2.securityId.match(reg);

        if (t1Matches[2] < t2Matches[2]) {
          return -1;
        }
        if (t1Matches[2] > t2Matches[2]) {
          return 1;
        } else {
          return +t1Matches[1] - +t2Matches[1];
        }
      });
  }

  public usTreasuryResponseToViewModel(
    response: USTreasuryResponse
  ): USTreasury {
    const { securityId, current, previous } = response;
    return {
      securityId,
      current: this.treasuryResponseDatatoViewModel(current),
      previous: this.treasuryResponseDatatoViewModel(previous)
    };
  }

  private treasuryResponseDatatoViewModel(
    responsePart: USTreasuryResponsePart | undefined
  ): USTreasuryData | undefined {
    if (responsePart) {
      return {
        ...omit(responsePart, 'date'),
        date: moment(responsePart.date).toDate()
      } as USTreasuryData;
    } else {
      return undefined;
    }
  }
}
